import { useEffect, useRef, useState } from 'react';
import { X, AlertCircle, GitBranch } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { formatRoleLabel } from '../../utils/roles';
import { normalizeRole } from '../../utils/roleNormalization';

export default function AssignLearningPathModal({
  learningPath,
  teamMembers,
  onClose,
  audienceLabel = 'team members',
  allowedRoleOptions = ['facility_admin', 'facility_manager', 'facility_worker'],
}) {
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const modalRef = useRef(null);
  const triggerRef = useRef(document.activeElement);

  useEffect(() => {
    const trigger = triggerRef.current;
    return () => {
      trigger?.focus();
    };
  }, []);

  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll(
        'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const getMemberRoles = (member) => {
    if (Array.isArray(member.roles)) return member.roles.map(normalizeRole);
    if (member.role) return [normalizeRole(member.role)];
    return [];
  };

  const filteredMembers = teamMembers.filter((member) => {
    const roles = getMemberRoles(member);
    if (!roles.length) return false;
    if (!roles.some((role) => allowedRoleOptions.includes(role))) return false;
    if (filterRole === 'all') return true;
    return roles.includes(filterRole);
  });

  const handleToggleMember = (memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId],
    );
  };

  const handleSelectAll = () => {
    if (selectedMembers.length === filteredMembers.length) {
      setSelectedMembers([]);
      return;
    }
    setSelectedMembers(filteredMembers.map((member) => member._id));
  };

  const handleAssign = async () => {
    if (!selectedMembers.length) {
      toast.error(`Please select at least one ${audienceLabel.replace(/s$/, '')}`);
      return;
    }

    try {
      setLoading(true);
      await api.post(`/lms/paths/${learningPath.learningPathId || learningPath._id}/assign`, {
        userIds: selectedMembers,
        dueDate: dueDate || null,
      });
      toast.success(`Learning path assigned to ${selectedMembers.length} ${audienceLabel}`);
      onClose();
    } catch (error) {
      console.error('Learning path assignment error:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to assign learning path');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 sm:p-6">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-learning-path-modal-title"
        tabIndex={-1}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-lg outline-none"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
          <div>
            <h2 id="assign-learning-path-modal-title" className="text-xl font-bold text-gray-900 sm:text-2xl">
              Assign Learning Path
            </h2>
            <p className="text-sm text-gray-600">{learningPath.title}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="px-4 py-6 sm:px-6">
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="flex items-center gap-2 text-sm text-blue-900">
              <GitBranch size={16} />
              <strong>Learning Path:</strong> {learningPath.title}
            </p>
            <p className="mt-1 text-sm text-blue-900">
              <strong>Mode:</strong> {String(learningPath.mode || '').replace(/_/g, ' ')}
            </p>
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-900">
              Path Due Date (Optional)
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              min={getMinDate()}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">Leave blank for no path due date</p>
          </div>

          <div>
            <div className="mb-4 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-900">
                Select {audienceLabel} ({selectedMembers.length} selected)
              </label>
            </div>

            <div className="mb-4">
              <label htmlFor="learning-path-filter-role" className="mb-1 block text-sm font-medium text-gray-900">
                Filter by role
              </label>
              <select
                id="learning-path-filter-role"
                value={filterRole}
                onChange={(event) => setFilterRole(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Roles</option>
                {allowedRoleOptions.map((role) => (
                  <option key={role} value={role}>
                    {formatRoleLabel(role)}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleSelectAll}
              className="mb-4 rounded border border-primary-200 px-3 py-1 text-sm font-medium text-primary-600 hover:bg-primary-50"
            >
              {selectedMembers.length === filteredMembers.length ? 'Deselect All' : 'Select All'}
            </button>

            <div className="max-h-96 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-4">
              {filteredMembers.length === 0 ? (
                <div className="py-8 text-center">
                  <AlertCircle className="mx-auto mb-2 text-gray-400" size={24} />
                  <p className="text-sm text-gray-500">No {audienceLabel} found</p>
                </div>
              ) : (
                filteredMembers.map((member) => (
                  <label
                    key={member._id}
                    className="flex cursor-pointer items-center gap-3 rounded p-2 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(member._id)}
                      onChange={() => handleToggleMember(member._id)}
                      className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-grow">
                      <p className="text-sm font-medium text-gray-900">
                        {member.firstName} {member.lastName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {getMemberRoles(member).map((role) => formatRoleLabel(role)).join(', ')}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={loading || selectedMembers.length === 0}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-400"
          >
            {loading && <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-t-2 border-white" />}
            Assign to {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
